import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { getMenuList } from '../api/services/systemService';
import { useSelector } from 'react-redux';
import { navigateToLogin } from '../utils';

const MenuBarContainer = styled.ul`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  list-style: none;
  margin: 0;
  height: 42px;
  background-color: white;
  border-top: 2px solid #d5d5d5;
  border-bottom: 2px solid #d5d5d5;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MenuItem = styled.li`
  display: flex;
  margin: 0 15px;
  font-weight: 600;
  font-size: 0.95rem;

  @media (max-width: 768px) {
    margin: 0 10px;
    white-space: nowrap;
    flex-shrink: 0;
    min-width: max-content;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin: 0 8px;
  }
`;

const MenuLink = styled.div`
  cursor: pointer;
  text-decoration: none;
  color: black;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    color: #c19330;
  }
`;

const NestedMenuItem = styled.div`
  break-inside: avoid;
  margin-bottom: 1rem;
  max-width: 220px;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 6vh;
  left: 50%;
  transform: translateX(-50%);
  overflow-y: auto;
  background-color: ${(props) => props.theme.colors.white};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  z-index: 999;
`;

const SingleColumnMenu = styled(DropdownMenu)`
  display: ${(props) => (props.isVisible ? 'flex' : 'none')};
  justify-content: space-between;
  align-items: flex-start;
  overflow-x: hidden;
  width: 100%;
  max-width: 1200px;
  height: 70vh;
  padding: 3vh 3vw;
  overflow-y: auto;
  background-color: ${(props) => props.theme.colors.white};
  top: 100%;
`;

const MenuGrid = styled.div`
  column-count: 4;
  column-gap: 20px;
  overflow-y: auto;
  margin-right: 20px;
`;

const SingleColumnItem = styled.div`
  cursor: pointer;
  padding: 7px 0;
  color: ${(props) =>
    props.sub ? props.theme.colors.menuText : props.theme.colors.black};
  font-size: ${(props) => (props.sub ? '0.85rem' : '1rem')};
  font-weight: ${(props) => (props.sub ? 'normal' : 'bold')};
  &:hover {
    text-decoration: underline;
  }
`;

const CategoryImage = styled.img`
  width: 250px;
  height: auto;
  object-fit: contain;
  margin-left: auto;
  margin-top: 3vh;
`;

export default function MenuBar() {
  const userInfo = useSelector((state) => state.userInfo);
  const navigate = useNavigate();
  const location = useLocation();

  const menuBarRef = useRef(null);

  const [menuList, setMenuList] = useState([]);
  const [hoveredMenuId, setHoveredMenuId] = useState(null);

  useEffect(() => {
    getDbMenuList();
  }, []);

  useEffect(() => {
    setHoveredMenuId(null);
  }, [location.pathname, location.search]);

  const handleMenuNavigate = (targetLink, navigationState) => {
    setHoveredMenuId(null);

    if (userInfo.userId != 0) {
      navigate(targetLink, {
        state: navigationState,
      });
    } else {
      navigateToLogin(navigate, targetLink);
    }
  };

  const getDbMenuList = async () => {
    const result = await getMenuList();
    const categoryMap = {};
    const menuList = [];
    // 모든 항목을 기본 구조로 맵에 먼저 구성
    result.menuList.forEach((cat) => {
      categoryMap[cat.idWebCate] = {
        id: cat.idWebCate,
        title: cat.cateNm,
        link: `/search?category=${encodeURIComponent(cat.cateNm.toLowerCase())}`,
        img_src: encodeURI(
          `${process.env.REACT_APP_FILE_UPLOAD_URL}${cat.filepath1}`,
        ),
        subMenu: {
          type: 'singleColumn',
          items: [],
        },
        parentId: cat.pCode, // Store parent ID for building hierarchy
        cateNm: cat.cateNm, // Store original name for building hierarchical URL
        level: cat.level, // Store the menu level (1, 2, 3, etc.)
      };
    });

    result.menuList.forEach((cat) => {
      if (cat.level === 1) {
        menuList.push(categoryMap[cat.idWebCate]);
      } else if (categoryMap[cat.pCode]) {
        // 부모가 있으면 그쪽에 붙임
        categoryMap[cat.pCode].subMenu.items.push(categoryMap[cat.idWebCate]);
      }
    });

    // Build hierarchical URL key for each menu item
    const buildUrlKey = (menuItem) => {
      const pathNames = [];
      let current = menuItem;

      while (current) {
        // Convert to URL-friendly format: keep only letters, replace spaces with hyphens
        const urlPart = current.cateNm
          .toLowerCase()
          .replace(/[^a-z\s]/g, '') // Remove all non-letter characters except spaces
          .replace(/\s+/g, '-') // Replace spaces with hyphens
          .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
          .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
        pathNames.unshift(urlPart);
        current = categoryMap[current.parentId];
      }

      return pathNames.join('-');
    };

    // First pass: Update all links with hierarchical URL keys
    Object.values(categoryMap).forEach((menuItem) => {
      const urlKey = buildUrlKey(menuItem);
      menuItem.link = `/search?category=${encodeURIComponent(urlKey)}`;
    });

    // Build breadcrumb paths for each menu item (after links are updated)
    const buildBreadcrumbPath = (menuItem) => {
      const path = [];
      let current = menuItem;

      while (current) {
        path.unshift({
          title: current.title,
          link: current.link,
        });
        current = categoryMap[current.parentId];
      }

      return path;
    };

    // Second pass: Add breadcrumb paths
    Object.values(categoryMap).forEach((menuItem) => {
      menuItem.breadcrumbPath = buildBreadcrumbPath(menuItem);
    });

    // 자식이 없으면 subMenu 제거
    function clean(menu) {
      if (menu.subMenu.items.length === 0) {
        delete menu.subMenu;
      } else {
        if (menu.subMenu.items.length > 1) {
          menu.subMenu.type = 'multiColumn';
        }
        menu.subMenu.items.forEach(clean);
      }
    }
    menuList.forEach(clean);
    setMenuList(menuList);
  };

  const handleMouseLeave = (e) => {
    const barTop = menuBarRef.current?.getBoundingClientRect().top;
    const mouseY = e.clientY;

    if (mouseY < barTop) {
      setHoveredMenuId(null); // 위로 벗어났을 때만 닫기
    }
  };

  const renderSubMenu = (menu) => {
    if (!menu || menu.length === 0) return null;
    const subMenuItems = menu.subMenu.items;
    const menuImgSrc = menu.img_src;
    const isVisible = hoveredMenuId === menu.id;
    return (
      <SingleColumnMenu
        isVisible={isVisible}
        onMouseEnter={() => setHoveredMenuId(menu.id)}
        onMouseLeave={() => {
          setHoveredMenuId(null);
        }}
      >
        <MenuGrid>
          {subMenuItems.map((subMenuItem) => (
            <NestedMenuItem key={subMenuItem.id}>
              <SingleColumnItem
                onClick={() => {
                  handleMenuNavigate(subMenuItem.link, {
                    displayTitle: subMenuItem.title,
                    breadcrumbPath: subMenuItem.breadcrumbPath,
                    menuLevel: subMenuItem.level,
                  });
                }}
              >
                {subMenuItem.title}
              </SingleColumnItem>
              {subMenuItem.subMenu?.items?.length > 0 &&
                subMenuItem.subMenu.items.map((subItem) => {
                  return (
                    <SingleColumnItem
                      key={subItem.id}
                      sub={true}
                      onClick={() => {
                        handleMenuNavigate(subItem.link, {
                          displayTitle: subItem.title,
                          breadcrumbPath: subItem.breadcrumbPath,
                          menuLevel: subItem.level,
                        });
                      }}
                    >
                      {subItem.title}
                    </SingleColumnItem>
                  );
                })}
            </NestedMenuItem>
          ))}
        </MenuGrid>
        <CategoryImage src={menuImgSrc} alt='Product' />
      </SingleColumnMenu>
    );
  };

  return (
    <>
      <MenuBarContainer>
        {menuList.map((menu) => (
          <MenuItem
            key={menu.id}
            ref={menuBarRef}
            onMouseEnter={() => {
              setHoveredMenuId(menu.id);
            }}
            onMouseLeave={handleMouseLeave}
          >
            <MenuLink
              onClick={() => {
                handleMenuNavigate(menu.link, {
                  displayTitle: menu.title,
                  breadcrumbPath: menu.breadcrumbPath,
                  menuLevel: menu.level,
                });
              }}
            >
              {menu.title}
            </MenuLink>
          </MenuItem>
        ))}
        {/* Shop By Brand Menu Item */}
        <MenuItem ref={menuBarRef}>
          <MenuLink
            onClick={() => {
              setHoveredMenuId(null);
              if (userInfo.userId != 0) {
                navigate('/brands');
              } else {
                navigateToLogin(navigate, '/brands');
              }
            }}
          >
            Shop By Brand
          </MenuLink>
        </MenuItem>
        {hoveredMenuId &&
          (() => {
            const selected = menuList.find((m) => m.id === hoveredMenuId);
            return selected?.subMenu ? renderSubMenu(selected) : null;
          })()}
      </MenuBarContainer>
    </>
  );
}
